import OtpInput from "react-otp-input";
import { Button, Form } from "antd";
import { useOtp } from "../../store/context/otp-context";
import { useState } from "react";
import { useTheme } from "../../store/context/theme-context";
import { useTenant } from "../../store/context/tenant-context";
import { OtpVerificationMethodEnum } from "../../shared/constants/constants";

const VerifyOTP = ({
  handleSubmit,
  handleResend,
  formData,
  isLoading,
  verificationMethod,
  onChangeMethod,
  title,
}) => {
  const [form] = Form.useForm();
  const { theme } = useTheme();
  const { otp, setOtp, otpError, seconds, isResendDisabled } = useOtp();

  const [focusedInput, setFocusedInput] = useState(null);

  const isDarkMode = theme === "dark";

  // Determine verification method message
  const getVerificationMessage = () => {
    const isAuthenticator =
      verificationMethod === OtpVerificationMethodEnum.AUTHENTICATOR_APP.value;

    if (isAuthenticator) {
      return {
        title: "Enter code from your authenticator app",
        description: "Open your authenticator app and enter the 6-digit code.",
      };
    }

    const isSMS = verificationMethod === OtpVerificationMethodEnum.SMS.value;
    const methodText = isSMS ? "mobile number" : "email";

    // For SMS, use maskedPhoneNumber if available, otherwise mask emailAddress
    let destination;
    if (isSMS && formData?.maskedPhoneNumber) {
      destination = formData.maskedPhoneNumber;
    } else if (formData?.emailAddress) {
      destination = `${formData.emailAddress.slice(0, 2)}******${formData.emailAddress.slice(
        formData.emailAddress.indexOf("@") - 2,
      )}`;
    } else {
      destination = `your ${methodText}`;
    }

    return {
      title: `We have sent a verification code to your ${methodText}`,
      destination,
      description: "Please enter the code below.",
    };
  };

  const message = getVerificationMessage();

  const containerStyle = {
    display: "flex",
    justifyContent: "start",
    gap: "8px",
  };

  const tenant = useTenant();
  const inputStyle = {
    width: "35px",
    height: "35px",
    fontSize: "14px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#ccc",
    borderRadius: "4px",
    textAlign: "center",
    outline: "none",
    transition: "border-color 0.3s",
  };
  const inputFocusStyle = {
    ...inputStyle,
    borderColor: tenant.theme.colorPrimary,
    boxShadow: "0 0 5px rgba(163, 42, 41, 0.5)",
  };

  const darkInputStyle = {
    ...inputStyle,
    borderColor: "#555",
    backgroundColor: "#333",
    color: "#fff",
  };
  // {`mb-2 focus:outline-none hover:text-[${tenant.theme.colorPrimary}]`}
  const darkInputFocusStyle = {
    ...darkInputStyle,
    borderColor: tenant.theme.colorPrimary,
    boxShadow: "0 0 5px rgba(163, 42, 41, 0.5)",
  };

  return (
    <div className="flex flex-col gap-3 justify-center items-center w-full">
      <div className="mt-4 mb-6">
        <p className="text-lg font-semibold">
          {title || message.title}
          {!title && message.destination && (
            <>
              <br />
              {message.destination}
            </>
          )}
        </p>

        <p className="text-base text-[#929497] mb-5">{message.description}</p>

        <div
          style={{ maxWidth: "150px", margin: "0 auto", textAlign: "center" }}
        >
          <img src={"/2fa.svg"} alt="Verification" style={{ width: "100%" }} />
        </div>
      </div>

      <Form form={form} layout="vertical">
        <div className="flex justify-center mt-4">
          <Form.Item
            label=""
            name="otp"
            rules={[
              {
                required: true,
                message: "Please enter the OTP.",
              },
            ]}
          >
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              renderSeparator={<span>-</span>}
              renderInput={(props, index) => (
                <input
                  {...props}
                  height={"80px !important"}
                  onFocus={() => setFocusedInput(index)}
                  onBlur={() => setFocusedInput(null)}
                  style={
                    focusedInput === index
                      ? isDarkMode
                        ? darkInputFocusStyle
                        : inputFocusStyle
                      : isDarkMode
                        ? darkInputStyle
                        : inputStyle
                  }
                />
              )}
              containerStyle={containerStyle}
            />
          </Form.Item>
        </div>

        <p className="my-3 text-center text-red-800">{otpError}</p>
        <div className="flex flex-col gap-1 mb-4 md:flex-row">
          <Button
            type="primary"
            htmlType="submit"
            className="px-3 py-2 w-full h-full text-base shadow-none"
            onClick={handleSubmit}
            disabled={otp.length < 6}
            loading={isLoading}
          >
            Verify
          </Button>
          <Button
            type="default"
            htmlType="button"
            onClick={handleResend}
            disabled={isResendDisabled}
            className="px-3 py-2 w-full h-full text-base"
          >
            {isResendDisabled
              ? ` Resend in 00:${seconds < 10 ? `0${seconds}` : seconds}`
              : "Resend"}
          </Button>
        </div>

        {/* Try Another Method Button */}
        {onChangeMethod && (
          <div className="mt-2 text-center">
            <Button
              type="link"
              htmlType="button"
              onClick={onChangeMethod}
              disabled={isLoading}
              className="text-sm"
              style={{ color: tenant.theme.colorPrimary }}
            >
              Try another verification method
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default VerifyOTP;
